import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { GOV_TYPE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientAuthApi, type TwoFactorStatus } from '../api';

/**
 * The second factor on this account — all of it.
 *
 * This screen used to offer one switch: a one-time code sent to the phone or
 * email at login. The other half has been on the server the whole time and had
 * no surface here — an authenticator app (set up, confirm, remove), recovery
 * codes for when the phone is lost, and trusted devices so a familiar browser
 * is not challenged every time. Seven endpoints that nothing called.
 *
 * A secret and a recovery code are each shown exactly once: the server stores
 * only hashes and cannot read either back, and the screen says so where it
 * matters rather than in a footnote.
 */

const cardStyle: React.CSSProperties = {
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
  padding: 18, boxShadow: T.shadow, fontFamily: T.font,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`,
  borderRadius: 10, fontSize: 13, fontFamily: T.font, color: T.text,
  outline: 'none', boxSizing: 'border-box',
};

const primaryBtn = (disabled = false): React.CSSProperties => ({
  padding: '10px 18px', borderRadius: 10, border: 'none', cursor: disabled ? 'default' : 'pointer',
  fontSize: 12, fontWeight: 700, fontFamily: T.font, color: T.white,
  background: T.teal, opacity: disabled ? 0.55 : 1,
});

const quietBtn: React.CSSProperties = {
  padding: '9px 16px', borderRadius: 10, border: `1px solid ${T.border}`,
  background: T.white, cursor: 'pointer', fontSize: 12, fontWeight: 600,
  color: T.textSec, fontFamily: T.font,
};

function fmtDate(v?: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function PatientTwoFactor() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Authenticator setup, alive only between "Set up" and "Confirm".
  const [setup, setSetup] = useState<{ secret: string; uri: string; note: string } | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [removeProof, setRemoveProof] = useState('');
  const [showRemove, setShowRemove] = useState(false);

  // Recovery codes come back once; they live in state until the patient leaves.
  const [codes, setCodes] = useState<string[] | null>(null);
  const [deviceLabel, setDeviceLabel] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setStatus(await patientAuthApi.twoFactorStatus());
    } catch (e: any) {
      setError(e?.body?.message || 'Your security settings could not be loaded — check your connection.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Render the provisioning URI as a QR so the authenticator can scan it.
  useEffect(() => {
    let alive = true;
    if (!setup?.uri) { setQrDataUrl(''); return; }
    import('qrcode')
      .then((QRCode) => QRCode.toDataURL(setup.uri, { margin: 1, width: 190 }))
      .then((url: string) => { if (alive) setQrDataUrl(url); })
      .catch(() => { if (alive) setQrDataUrl(''); });
    return () => { alive = false; };
  }, [setup]);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key); setError(''); setNotice('');
    try { await fn(); }
    catch (e: any) { setError(e?.body?.errors?.[0]?.message || e?.body?.message || 'That could not be done — please try again.'); }
    finally { setBusy(''); }
  };

  const toggleOtp = () => run('otp', async () => {
    const next = !status?.otpStepUpEnabled;
    const res = await patientAuthApi.setTwoFactor(next);
    setStatus((s) => (s ? { ...s, otpStepUpEnabled: res.twoFactorEnabled } : s));
    setNotice(res.twoFactorEnabled
      ? 'A one-time code will now be required after your password.'
      : 'The one-time code step is off.');
  });

  const beginSetup = () => run('setup', async () => {
    setSetup(await patientAuthApi.totpSetup());
    setConfirmCode('');
  });

  const confirmSetup = () => run('confirm', async () => {
    if (confirmCode.trim().length < 4) { throw { body: { message: 'Enter the 6-digit code from your authenticator.' } }; }
    setStatus(await patientAuthApi.totpConfirm(confirmCode));
    setSetup(null); setConfirmCode('');
    setNotice('Your authenticator app is switched on.');
  });

  const removeTotp = () => run('remove', async () => {
    if (removeProof.trim().length < 4) { throw { body: { message: 'Enter a current code, or one recovery code, to confirm.' } }; }
    setStatus(await patientAuthApi.totpRemove(removeProof));
    setRemoveProof(''); setShowRemove(false);
    setNotice('The authenticator has been removed from this account.');
  });

  const newCodes = () => run('codes', async () => {
    const r = await patientAuthApi.generateRecoveryCodes();
    setCodes(r.codes);
    await load();
  });

  const trustThis = () => run('trust', async () => {
    const label = deviceLabel.trim() || `This browser · ${new Date().toLocaleDateString('en-IN')}`;
    setStatus(await patientAuthApi.trustDevice(label));
    setDeviceLabel('');
    setNotice('This device will not be asked for a second factor.');
  });

  const revokeDevice = (id: string) => run(`revoke-${id}`, async () => {
    setStatus(await patientAuthApi.revokeTrustedDevice(id));
    setNotice('That device will be challenged again next time.');
  });

  const totpOn = status?.totp?.confirmed === true;

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h1 style={{ ...GOV_TYPE.pageTitle }}>Two-step verification</h1>
        <p style={{ fontSize: 11.5, color: T.textSec, marginTop: 4, fontFamily: T.font, lineHeight: 1.5 }}>
          A second proof after your password. Keep at least one method on, and keep your recovery codes somewhere safe.
        </p>
      </div>

      {error && (
        <div style={{ ...cardStyle, borderColor: `${T.danger}55`, padding: 12, fontSize: 12, color: T.danger }}>
          {error}
        </div>
      )}
      {notice && (
        <div style={{ ...cardStyle, borderColor: `${T.teal}55`, padding: 12, fontSize: 12, color: T.teal }}>
          {notice}
        </div>
      )}

      {loading ? (
        <div style={{ ...cardStyle, textAlign: 'center', fontSize: 12, color: T.textSec }}>
          Loading your security settings…
        </div>
      ) : !status ? (
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <button onClick={() => void load()} style={primaryBtn()}>Retry</button>
        </div>
      ) : (
        <>
          {/* ── One-time code at login ─────────────────────────────── */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Code by SMS or email</div>
                <div style={{ fontSize: 11, color: T.textSec, marginTop: 4, lineHeight: 1.5 }}>
                  After your password, a one-time code goes to{' '}
                  {status.phone ? `your phone ending ${status.phone.slice(-4)}` : 'your registered number'}
                  {status.email ? ` or ${status.email}` : ''}.
                </div>
              </div>
              <button
                onClick={toggleOtp}
                disabled={busy === 'otp'}
                style={{
                  ...primaryBtn(busy === 'otp'),
                  background: status.otpStepUpEnabled ? T.white : T.teal,
                  color: status.otpStepUpEnabled ? T.textSec : T.white,
                  border: status.otpStepUpEnabled ? `1px solid ${T.border}` : 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {busy === 'otp' ? 'Saving…' : status.otpStepUpEnabled ? 'Turn off' : 'Turn on'}
              </button>
            </div>
          </div>

          {/* ── Authenticator app ──────────────────────────────────── */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Authenticator app</div>
                <div style={{ fontSize: 11, color: T.textSec, marginTop: 4, lineHeight: 1.5 }}>
                  {totpOn
                    ? `On since ${fmtDate(status.totp.confirmedAt) || 'recently'} — codes come from your app, and work with no signal.`
                    : 'Codes from Google Authenticator, Authy or any similar app. Works without a network.'}
                </div>
              </div>
              {!totpOn && !setup && (
                <button onClick={beginSetup} disabled={busy === 'setup'} style={{ ...primaryBtn(busy === 'setup'), whiteSpace: 'nowrap' }}>
                  {busy === 'setup' ? 'Starting…' : 'Set up'}
                </button>
              )}
              {totpOn && (
                <button onClick={() => setShowRemove((v) => !v)} style={{ ...quietBtn, whiteSpace: 'nowrap' }}>
                  Remove
                </button>
              )}
            </div>

            {setup && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {qrDataUrl && (
                    <img src={qrDataUrl} alt="Authenticator setup QR code" width={160} height={160} style={{ borderRadius: 10 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 11, color: T.textSec, lineHeight: 1.6, marginBottom: 8 }}>{setup.note}</div>
                    <div style={{ fontSize: 10, color: T.textTer, marginBottom: 4 }}>Or enter this key by hand</div>
                    <div style={{
                      fontFamily: 'monospace', fontSize: 12.5, fontWeight: 700, color: T.text,
                      background: T.ice, border: `1px solid ${T.border}`, borderRadius: 8,
                      padding: '8px 10px', wordBreak: 'break-all', marginBottom: 12,
                    }}>
                      {setup.secret}
                    </div>
                    <label htmlFor="totp-confirm" style={{ fontSize: 11, fontWeight: 600, color: T.textSec, display: 'block', marginBottom: 6 }}>
                      Code from the app
                    </label>
                    <input
                      id="totp-confirm"
                      inputMode="numeric"
                      placeholder="6-digit code"
                      value={confirmCode}
                      onChange={(e) => setConfirmCode(e.target.value)}
                      style={inputStyle}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button onClick={confirmSetup} disabled={busy === 'confirm'} style={primaryBtn(busy === 'confirm')}>
                        {busy === 'confirm' ? 'Checking…' : 'Confirm and switch on'}
                      </button>
                      <button onClick={() => { setSetup(null); setConfirmCode(''); }} style={quietBtn}>Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showRemove && totpOn && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
                <label htmlFor="totp-remove" style={{ fontSize: 11, fontWeight: 600, color: T.textSec, display: 'block', marginBottom: 6 }}>
                  Enter a current code, or one recovery code, to confirm
                </label>
                <input
                  id="totp-remove"
                  placeholder="Code"
                  value={removeProof}
                  onChange={(e) => setRemoveProof(e.target.value)}
                  style={inputStyle}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button
                    onClick={removeTotp}
                    disabled={busy === 'remove'}
                    style={{ ...primaryBtn(busy === 'remove'), background: T.danger }}
                  >
                    {busy === 'remove' ? 'Removing…' : 'Remove authenticator'}
                  </button>
                  <button onClick={() => { setShowRemove(false); setRemoveProof(''); }} style={quietBtn}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Recovery codes ─────────────────────────────────────── */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Recovery codes</div>
                <div style={{ fontSize: 11, color: T.textSec, marginTop: 4, lineHeight: 1.5 }}>
                  {status.recoveryCodes.total > 0
                    ? `${status.recoveryCodes.remaining} of ${status.recoveryCodes.total} left, generated ${fmtDate(status.recoveryCodes.generatedAt) || 'earlier'}.`
                    : 'None yet. These are how you get in if you lose your phone.'}
                </div>
              </div>
              <button onClick={newCodes} disabled={busy === 'codes'} style={{ ...primaryBtn(busy === 'codes'), whiteSpace: 'nowrap' }}>
                {busy === 'codes' ? 'Generating…' : status.recoveryCodes.total > 0 ? 'Generate new' : 'Generate'}
              </button>
            </div>

            {codes && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 11, color: T.danger, fontWeight: 700, marginBottom: 8 }}>
                  Save these now. They are stored hashed and cannot be shown again.
                </div>
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8,
                  background: T.ice, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12,
                }}>
                  {codes.map((c) => (
                    <div key={c} style={{ fontFamily: 'monospace', fontSize: 12.5, fontWeight: 700, color: T.text }}>{c}</div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button
                    onClick={() => { void navigator.clipboard?.writeText(codes.join('\n')); setNotice('Recovery codes copied.'); }}
                    style={quietBtn}
                  >
                    Copy all
                  </button>
                  <button onClick={() => setCodes(null)} style={quietBtn}>I have saved them</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Trusted devices ────────────────────────────────────── */}
          <div style={cardStyle}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Trusted devices</div>
            <div style={{ fontSize: 11, color: T.textSec, marginTop: 4, lineHeight: 1.5 }}>
              A device you trust is not asked for a second factor. Remove any you do not recognise.
            </div>

            {status.trustedDevices.length === 0 ? (
              <div style={{ fontSize: 11.5, color: T.textTer, marginTop: 12 }}>No trusted devices.</div>
            ) : (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {status.trustedDevices.map((d) => (
                  <div
                    key={d.id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                      background: T.ice, border: `1px solid ${T.border}`, borderRadius: 10, padding: '9px 12px',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.label}
                      </div>
                      {(d.addedAt || d.lastSeenAt) && (
                        <div style={{ fontSize: 10, color: T.textTer }}>
                          {d.lastSeenAt ? `Last seen ${fmtDate(d.lastSeenAt)}` : `Added ${fmtDate(d.addedAt)}`}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => revokeDevice(d.id)}
                      disabled={busy === `revoke-${d.id}`}
                      style={{ ...quietBtn, color: T.danger, borderColor: `${T.danger}40`, whiteSpace: 'nowrap' }}
                    >
                      {busy === `revoke-${d.id}` ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <input
                aria-label="Name for this device"
                placeholder="Name this device (optional)"
                value={deviceLabel}
                onChange={(e) => setDeviceLabel(e.target.value)}
                style={{ ...inputStyle, flex: 1, minWidth: 160 }}
              />
              <button onClick={trustThis} disabled={busy === 'trust'} style={primaryBtn(busy === 'trust')}>
                {busy === 'trust' ? 'Adding…' : 'Trust this device'}
              </button>
            </div>
          </div>

          <button onClick={() => navigate('/patient/security')} style={{ ...quietBtn, alignSelf: 'flex-start' }}>
            Back to security
          </button>
        </>
      )}
    </div>
  );
}
